import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { RadioGroupItemProps } from '@capsule/consumer-design-system';
import { RadioButtonGroup, SubmitButton, Typography } from '@capsule/consumer-design-system';

import { useMediaQuery } from '@react-hookz/web';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { Form, Formik } from 'formik';
import debounce from 'lodash/debounce';
import { useTranslations } from 'next-intl';
import { useLocation } from 'react-router';
import { css, useTheme } from 'styled-components';
import * as Yup from 'yup';

import capsuleLogo from 'public/capsulerx-logo.svg';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import { Skeleton } from 'src/app/components/Elements/Skeleton';
import { usePharmacySearch } from 'src/app/hooks/data/pharmacy';
import type { PharmacyHubEntryResponse } from 'src/app/hooks/data/pharmacyHub';
import { useSelectPharmacyForPendingEscripts } from 'src/app/hooks/data/pharmacyHub';
import { useUpdateCommunicationPreferences } from 'src/app/hooks/data/useCommunicationPreferences';
import useAuth from 'src/app/hooks/useAuth';
import { useIsSpanishRoute } from 'src/app/hooks/useIsSpanishRoute';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import type { PlaceDetail, PlaceDetailsResponse } from 'src/app/types';
import { CAPSULE_ICON_STRINGS, FORM_INPUT_ICON_STRINGS } from 'src/consts';

import { ClearRxLayout } from '../ClearRxLayout';
import { CLEARRX_PHONE, LANGUAGES_SUPPORTED } from '../constants';
import { FauxLabel, FormikTextFieldWithTextareaStyles, TextFieldTransparentButton } from '../styles';

import { SearchListItem } from './SearchListItem';
import { CapsuleLogo, ListContainer, MedInStockTag, PharmacySearchContainer } from './styles';

import type { SelectedPharmacy } from '.';

type ConfirmPharmacyProps = {
  setSelectedPharmacy: Dispatch<SetStateAction<SelectedPharmacy>>;
  refetchHubEscriptsOnSuccess: () => void;
  pendingEscriptId: string;
  pendingEscripts: PharmacyHubEntryResponse;
  isFetchingPreviousPharmacyDetails: boolean;
  previousPharmacyGooglePlaceDetails?: PlaceDetailsResponse;
};

export type PlaceDetailsCache = {
  [placeId: string]: PlaceDetailsResponse;
};

export interface PlaceDetailExtended extends PlaceDetail {
  hours?: string;
}

export const ConfirmPharmacy: React.FC<ConfirmPharmacyProps> = ({
  setSelectedPharmacy,
  refetchHubEscriptsOnSuccess,
  pendingEscriptId,
  pendingEscripts,
  isFetchingPreviousPharmacyDetails,
  previousPharmacyGooglePlaceDetails,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isSpanishRoute = useIsSpanishRoute();
  const { pathname, search } = location;
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const userUuid = params.get('userUuid');

  const { logout, loginByCode } = useAuth();
  const t = useTranslations('pharmacySelection.confirm');
  /* Functionality and state for the text area element */
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const textFieldBackButtonRef = useRef<HTMLButtonElement | null>(null);
  const textFieldClearButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isFullScreen, setFullScreen] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [pharmacySearchInput, setPharmacySearchInput] = useState<string>('');
  const [shouldShowResults, setShouldShowResults] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<PlaceDetail[]>([]);
  const [placeDetailsResponseCache, setPlaceDetailsResponseCache] = useState<PlaceDetailsCache>({});
  // Track submitting state here instead of using formik state since we force logout
  // on auth error to retry and dont want to override the submit state
  const [isSubmittingWithRetry, setIsSubmittingWithRetry] = useState<boolean>(false);
  const numPendingEscripts = pendingEscripts.medications.length;
  const dayOfTheWeek = new Date().getDay();

  const hasSameDayDelivery = !!pendingEscripts.windows?.has_same_day_delivery;
  const hasNextDayDelivery = !!pendingEscripts.windows?.has_next_day_delivery;
  const isCapsuleAvailable = !!pendingEscripts.can_show_capsule && (hasSameDayDelivery || hasNextDayDelivery);
  const capsuleIneligibilityReason = pendingEscripts.capsule_ineligibility_reason;

  const isMobile = useMediaQuery(theme.breakpoints.smallDown) ?? false;
  const searchBoxLeftIcon =
    !isFullScreen && typeof document !== undefined && !(textInputRef.current === document.activeElement)
      ? FORM_INPUT_ICON_STRINGS['system_search']
      : FORM_INPUT_ICON_STRINGS['chevron_left'];

  const dispatchViewSelectPharmacyEvent = useSegmentEvent('viewClearRxSelectPharmacy', { shouldTrackOnMount: false });
  const dispatchSelectClearRxPharmacyEvent = useSegmentEvent('selectClearRxPharmacy', { shouldTrackOnMount: false });
  const dispatchViewSearchClearRxPharmacy = useSegmentEvent('viewSearchClearRxPharmacy', { shouldTrackOnMount: false });
  const dispatchErrorEvent = useSegmentEvent('error', { shouldTrackOnMount: false });
  const dispatchfieldFocused = useSegmentEvent('fieldFocused', { shouldTrackOnMount: false });

  const { mutateAsync: submitSelectedPharmacy } = useSelectPharmacyForPendingEscripts();

  const { mutateAsync: updateCommunicationPreference } = useUpdateCommunicationPreferences();

  const {
    data: pharmacyResults,
    refetch: refetchPharmacyResults,
    isFetching: isFetchingPharmacyResults,
  } = usePharmacySearch(
    pharmacySearchInput,
    pendingEscripts.latitude && pendingEscripts.longitude
      ? `${pendingEscripts.latitude},${pendingEscripts.longitude}`
      : '',
    10000, // 10k meters or 6 mile radius
    { type: 'pharmacy' }
  );

  const PREVIOUSLY_PREFERRED_OPTION_VALUE = 'previously_preferred';
  const CAPSULE_OPTION_VALUE = 'capsule';
  const [options, setOptions] = useState<RadioGroupItemProps[]>([]);
  const SEARCH_PHARMACY_INPUT_NAME = 'searchPharmName';

  const validationSchema = Yup.object({
    pharmacy: Yup.string().required(t('selectError')).default(''),
  });
  const initialValues = validationSchema.cast({});

  const setUpInitialPharmacyOptions = useCallback(async () => {
    if (isFetchingPreviousPharmacyDetails || !!pendingEscripts?.selected_pharmacy?.pharmacy_selected_at) return;

    const initialOptions: RadioGroupItemProps[] = [];
    const initialCache: PlaceDetailsCache = {};
    let formattedPrevAddress = '';
    const previousPharmacy = pendingEscripts.previous_pharmacy;

    if (isCapsuleAvailable) {
      initialOptions.push({
        label: <CapsuleLogo src={capsuleLogo} />,
        value: CAPSULE_OPTION_VALUE,
        description: t('freeDeliveryText', { zipCode: pendingEscripts.zip_code || '' }),
        subDescription: (
          <MedInStockTag>
            {t('medsInStockText', {
              count: numPendingEscripts.toString(),
              total: numPendingEscripts.toString(),
            })}
          </MedInStockTag>
        ),
        descriptionColor: 'capsuleGreen60',
      });
      initialCache[CAPSULE_OPTION_VALUE] = {
        place_id: CAPSULE_OPTION_VALUE,
        formatted_address: pendingEscripts.facility_name || '',
        name: 'Capsule',
        opening_hours: {},
        status: '',
      };
    }

    // If previous pharmacy is capsule, do not show as option
    const shouldUsePreviousPharmacy =
      previousPharmacy?.name && !previousPharmacy?.name.toLowerCase().startsWith('capsule');
    const shouldUsePreviousPharmacyDetailsFromGoogleApi =
      previousPharmacyGooglePlaceDetails && shouldUsePreviousPharmacy;

    if (shouldUsePreviousPharmacyDetailsFromGoogleApi || shouldUsePreviousPharmacy) {
      // Use EMR previous pharmacy address and name format regardless if found in google places

      if (previousPharmacy.address1) formattedPrevAddress += previousPharmacy.address1;
      if (previousPharmacy.city) formattedPrevAddress += `, ${previousPharmacy.city}`;
      if (previousPharmacy.state) formattedPrevAddress += `, ${previousPharmacy.state}`;
      if (previousPharmacy.zipcode) formattedPrevAddress += `, ${previousPharmacy.zipcode}`;

      const pharmacyHours = shouldUsePreviousPharmacyDetailsFromGoogleApi
        ? previousPharmacyGooglePlaceDetails.opening_hours?.weekday_text?.[dayOfTheWeek - 1]
        : undefined;

      initialOptions.push({
        label: previousPharmacy.name,
        value: PREVIOUSLY_PREFERRED_OPTION_VALUE,
        description: formattedPrevAddress,
        subDescription: pharmacyHours,
      });

      // This cache data is to prevent unnecessary place api calls for the same place id
      // and is also used on submission to format pharmacy data
      //
      // Note: overwriting the google places name and formatted address to use the EMR data
      // In case we need to reissue the escript, the name and address should match the EMR data
      // to be searched easier
      initialCache[PREVIOUSLY_PREFERRED_OPTION_VALUE] = shouldUsePreviousPharmacyDetailsFromGoogleApi
        ? {
            ...previousPharmacyGooglePlaceDetails,
            formatted_address: formattedPrevAddress,
            name: previousPharmacy.name,
          }
        : {
            place_id: PREVIOUSLY_PREFERRED_OPTION_VALUE,
            formatted_address: formattedPrevAddress,
            name: previousPharmacy.name,
            opening_hours: {},
            status: '',
          };
    }

    setOptions(initialOptions);
    setPlaceDetailsResponseCache(initialCache);
    dispatchViewSelectPharmacyEvent({
      referral: typeof window !== 'undefined' ? document.referrer : null,
      same_day_availability: hasSameDayDelivery,
      capsule_available: isCapsuleAvailable,
      capsule_ineligibility_reason: capsuleIneligibilityReason,
      previously_preferred_pharmacy_name: previousPharmacy?.name || '',
      previously_preferred_pharmacy_address: shouldUsePreviousPharmacyDetailsFromGoogleApi
        ? previousPharmacyGooglePlaceDetails.formatted_address
        : formattedPrevAddress,
      provider_npi: pendingEscripts.provider.npi,
      provider_name: `${pendingEscripts.provider.first_name} ${pendingEscripts.provider.last_name}`,
      practice_name: pendingEscripts.provider.practice_name ?? '',
      emr_department_name: pendingEscripts.department?.name || '',
      emr_department_id: pendingEscripts.department?.emr_department_id || '',
      language_used: isSpanishRoute ? LANGUAGES_SUPPORTED.SPANISH : LANGUAGES_SUPPORTED.ENGLISH,
    });
  }, [previousPharmacyGooglePlaceDetails, isFetchingPreviousPharmacyDetails, pendingEscripts, dayOfTheWeek]);

  useEffect(() => {
    setUpInitialPharmacyOptions();
  }, [setUpInitialPharmacyOptions]);

  useEffect(() => {
    if (shouldShowResults || isFetchingPharmacyResults) {
      textInputRef?.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
        inline: 'nearest',
      });
    }
  }, [shouldShowResults, isFetchingPharmacyResults]);

  useEffect(() => {
    if (shouldShowResults) {
      dispatchViewSearchClearRxPharmacy();
    }
  }, [shouldShowResults, dispatchViewSearchClearRxPharmacy]);

  useEffect(() => {
    if (isFullScreen && isMobile) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = '';
    }
  }, [isFullScreen, isMobile]);

  const debouncedRefetch = useCallback(
    debounce(() => refetchPharmacyResults(), 400),
    [refetchPharmacyResults]
  );

  useEffect(() => {
    if (pharmacySearchInput.length > 0) {
      debouncedRefetch();
      setShouldShowResults(true);
    } else {
      setSearchResults([]);
    }
    if (isMobile && pharmacySearchInput.length > 0) {
      setFullScreen(true);
    }
  }, [pharmacySearchInput]);

  useEffect(() => {
    if (pharmacySearchInput.length > 0) {
      // // Only return the first 5 pharmacy results since we are
      // // making an additional detail request for hours and phone
      const fullList = pharmacyResults?.data?.results || [];
      setSearchResults(fullList.slice(0, 5));
    }
  }, [pharmacyResults?.data?.results]);

  const handlePharmacySearchOnChange = () => {
    setPharmacySearchInput(textInputRef?.current?.value || '');
  };

  const handleOnFocus = () => {
    dispatchfieldFocused({
      pagepath: pathname,
      fieldname: SEARCH_PHARMACY_INPUT_NAME,
    });
    if (isMobile) {
      if (pharmacySearchInput.length > 0) {
        setFullScreen(true);
        if (searchResults.length > 0) setShouldShowResults(true);
      }
    } else {
      if (pharmacySearchInput.length > 0) setShouldShowResults(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (textInputRef.current === null) return;
    switch (e.key) {
      case 'Enter':
        // Prevent submitting form on pharmacy search enter key down
        refetchPharmacyResults();
        break;
    }
  };

  return (
    <MarketingPageSection>
      <ClearRxLayout>
        <Typography
          variant="heading1"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s4};
          `}
        >
          {t('title')}
        </Typography>
        <Typography
          variant="body"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s6};
          `}
        >
          {t('subtitle')}
        </Typography>
        {isFetchingPreviousPharmacyDetails ? (
          <Skeleton />
        ) : (
          <Formik
            initialValues={initialValues}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={async (values, { setErrors }) => {
              setIsSubmittingWithRetry(true);

              const selectedOption = placeDetailsResponseCache[values.pharmacy];
              if (!selectedOption) {
                dispatchErrorEvent({
                  errorMessage: 'Error Submitting Selected Pharmacy',
                  errorSummary: 'Selected option not found on place details cache',
                  api: null,
                  errorCode: null,
                  source: pathname,
                });
                setErrors({
                  pharmacy: `Error submitting selected pharmacy, please try again or contact us at ${CLEARRX_PHONE}.`,
                });
                return;
              }

              const selectedPharmacy = {
                name: selectedOption.name,
                phone: selectedOption.formatted_phone_number,
                address: selectedOption.formatted_address,
              };
              await setSelectedPharmacy(selectedPharmacy);

              const isCapsuleSelected = selectedPharmacy.name === 'Capsule';
              dispatchSelectClearRxPharmacyEvent({
                referral: typeof window !== 'undefined' ? document.referrer : null,
                is_capsule: isCapsuleSelected,
                from_search: ![CAPSULE_OPTION_VALUE, PREVIOUSLY_PREFERRED_OPTION_VALUE].includes(values.pharmacy),
                pharmacy_name: selectedPharmacy.name,
                pharmacy_address: selectedPharmacy.address,
                provider_npi: pendingEscripts.provider.npi,
                provider_name: `${pendingEscripts.provider.first_name} ${pendingEscripts.provider.last_name}`,
                practice_name: pendingEscripts.provider.practice_name ?? '',
                emr_department_name: pendingEscripts.department?.name || '',
                emr_department_id: pendingEscripts.department?.emr_department_id || '',
                language_used: isSpanishRoute ? LANGUAGES_SUPPORTED.SPANISH : LANGUAGES_SUPPORTED.ENGLISH,
              });

              const submitSelectedPharmacyWithAuthRetry = async (isRetry = false) => {
                try {
                  await submitSelectedPharmacy({
                    isCapsuleSelected: isCapsuleSelected,
                    entryUUID: pendingEscriptId,
                    pharmacyName: selectedPharmacy.name,
                    pharmacyAddress: selectedPharmacy.address,
                    pharmacyPhone: selectedPharmacy.phone,
                    additional_pharmacy_hub_pending_escript_uuids:
                      pendingEscripts.additional_pharmacy_hub_pending_escript_uuids || [],
                  });
                  // Refetch escript data for success information
                  // instead of via state
                  await refetchHubEscriptsOnSuccess();
                  setIsSubmittingWithRetry(false);
                } catch (e) {
                  if (isAxiosError(e)) {
                    if (
                      e?.response?.status == 400 &&
                      e?.response?.data?.flavorText?.detail?.includes('Authentication credentials were not provided')
                    ) {
                      if (isRetry === false && userUuid && code) {
                        console.log('Auth error found on submission, retrying');
                        await loginByCode(userUuid, code);
                        submitSelectedPharmacyWithAuthRetry(true);
                        return;
                      }

                      // If we have no credentials, force logout and require reauthentication
                      console.log('Auth error found on submission, logging out');
                      logout();
                    } else {
                      setErrors({
                        pharmacy: `Error submitting selected pharmacy, please try again or contact us at ${CLEARRX_PHONE}.`,
                      });
                      setIsSubmittingWithRetry(false);
                    }
                    dispatchErrorEvent({
                      errorMessage: 'Error Submitting Selected Pharmacy',
                      errorSummary: `${e?.response?.data?.flavorText}`,
                      api: e?.request?.responseURL,
                      errorCode: e?.response?.status ?? null,
                      source: pathname,
                    });
                  }
                }
                try {
                  await updateCommunicationPreference({
                    patient_id: pendingEscripts.patient.id,
                    preferred_language: isSpanishRoute ? 'Spanish' : 'English',
                  });
                } catch (error) {
                  console.error('Error updating communication preference', error);
                  Sentry.captureException(error);
                }
              };
              submitSelectedPharmacyWithAuthRetry();
            }}
          >
            {formikBag => {
              const handleSelectPharmacySearchItem = async (item: PlaceDetailExtended) => {
                // This ensures we always re render even if options arent updated
                // for the updated selected item
                await setIsLoadingOptions(true);

                setPharmacySearchInput('');
                formikBag.setFieldValue(SEARCH_PHARMACY_INPUT_NAME, '');
                const isItemAlreadySelected = options.find(opt => opt.value == item.place_id);
                if (!isItemAlreadySelected) {
                  await setOptions(
                    options.concat([
                      {
                        label: item.name,
                        value: item.place_id,
                        description: item.formatted_address,
                        subDescription: item.hours,
                      },
                    ])
                  );
                }
                formikBag.setFieldValue('pharmacy', item.place_id);
                setIsLoadingOptions(false);
                setShouldShowResults(false);
                setFullScreen(false);
              };
              return (
                <Form>
                  {isLoadingOptions ? (
                    <Skeleton />
                  ) : (
                    <RadioButtonGroup
                      css={css`
                        margin-bottom: ${({ theme }) => theme.space.s5};
                      `}
                      onChange={formikBag.handleChange}
                      label={t('selectLabel')}
                      error={formikBag.errors.pharmacy}
                      isValid={!formikBag.errors.pharmacy || !formikBag.touched.pharmacy}
                      options={options}
                      name={'pharmacy'}
                      defaultValue={formikBag.values.pharmacy}
                    />
                  )}
                  <PharmacySearchContainer isFullScreen={isFullScreen} isMobile={isMobile}>
                    <FauxLabel
                      variant="overline"
                      hasError={false}
                      isFocused={textInputRef.current === document.activeElement}
                    >
                      {t('findAnotherPharmacy')}
                    </FauxLabel>
                    <FormikTextFieldWithTextareaStyles
                      name={SEARCH_PHARMACY_INPUT_NAME}
                      onBlur={e => {
                        // Duplicates logic in the actual buttons because the TextInput intercepts clicks on blur.
                        if (e.relatedTarget === textFieldBackButtonRef.current) {
                          if (searchResults.length == 0) {
                            setShouldShowResults(false);
                          }
                          if (isMobile) {
                            setFullScreen(false);
                            setShouldShowResults(false);
                          }
                        } else if (e.relatedTarget === textFieldClearButtonRef.current && !!textInputRef.current) {
                          formikBag.setFieldValue(SEARCH_PHARMACY_INPUT_NAME, '');
                        }
                      }}
                      ref={textInputRef}
                      label={{
                        content: t('searchPlaceholder'),
                        isHidden: true,
                      }}
                      adornment={
                        isMobile && {
                          left: (
                            <>
                              <TextFieldTransparentButton
                                type="button"
                                onClick={() => {
                                  if (isFullScreen) {
                                    formikBag.setFieldValue(SEARCH_PHARMACY_INPUT_NAME, '');
                                    setPharmacySearchInput('');
                                    setShouldShowResults(false);
                                    setFullScreen(false);
                                  }
                                }}
                                ref={textFieldBackButtonRef}
                              />
                              <CapsuleIcon
                                iconString={searchBoxLeftIcon}
                                css={css`
                                  padding: 0px !important;
                                  height: 36px !important;
                                  width: 36px !important;
                                  svg {
                                    height: 36px !important;
                                    width: 36px !important;
                                  }
                                  margin-bottom: ${({ theme }) => theme.space.s3};
                                `}
                              />
                            </>
                          ),
                          right: (
                            <>
                              <TextFieldTransparentButton
                                type="button"
                                onClick={() => {
                                  formikBag.setFieldValue(SEARCH_PHARMACY_INPUT_NAME, '');
                                  setPharmacySearchInput('');
                                  textInputRef?.current?.focus();
                                }}
                                ref={textFieldClearButtonRef}
                              />
                              <CapsuleIcon
                                iconString={CAPSULE_ICON_STRINGS.close}
                                css={css`
                                  padding: 0px !important;
                                  height: 36px !important;
                                  width: 36px !important;
                                  svg {
                                    height: 36px !important;
                                    width: 36px !important;
                                  }
                                  margin-bottom: ${({ theme }) => theme.space.s3};
                                `}
                              />
                            </>
                          ),
                        }
                      }
                      placeholder={t('searchPlaceholder')}
                      onFocus={handleOnFocus}
                      onKeyDown={handleKeyDown}
                      onChange={handlePharmacySearchOnChange}
                      autoComplete="off"
                      type="text"
                    />
                    <ListContainer toggleShow={shouldShowResults} isFullScreen={isFullScreen}>
                      {isFetchingPharmacyResults && shouldShowResults && <Skeleton />}
                      {!isFetchingPharmacyResults &&
                        shouldShowResults &&
                        searchResults.length > 0 &&
                        searchResults?.map(item => (
                          <SearchListItem
                            item={item}
                            selectItem={handleSelectPharmacySearchItem}
                            setPlaceDetailsCache={setPlaceDetailsResponseCache}
                            placeDetailsCache={placeDetailsResponseCache}
                            dayOfTheWeek={dayOfTheWeek}
                            closesAtText={t('closesAtText')}
                          />
                        ))}
                      {!isFetchingPharmacyResults &&
                        shouldShowResults &&
                        pharmacyResults?.data?.results?.length === 0 &&
                        pharmacySearchInput.length > 0 && <Typography>{t('noResults')}</Typography>}
                    </ListContainer>
                  </PharmacySearchContainer>
                  <SubmitButton
                    isSubmitting={isSubmittingWithRetry}
                    isFullWidth
                    variant="contained-secondary-lg"
                    css={css`
                      margin-top: ${({ theme }) => theme.space.s7};
                    `}
                  >
                    {t('confirmButton')}
                  </SubmitButton>
                </Form>
              );
            }}
          </Formik>
        )}
      </ClearRxLayout>
    </MarketingPageSection>
  );
};
